import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalysisSelect, AnalysisSubHeader } from '../../../../components';
import type { MediaMetric } from '../../model/aggregate';
import { buildBarRows } from '../../model/bars';
import { dimensionColor } from '../../model/dimensionColor';
import { displayDimensions, type DimensionKind, type DimensionRow } from '../../model/dimensions';
import { formatMetricValue, formatSharePercent } from '../../model/format';
import type { OverIndexInsight } from '../../model/insights';
import { carouselPosition } from '../../model/carousel';
import { InsightCarousel } from '../InsightCarousel';

interface SpendByDimensionProps {
  rows: DimensionRow[];
  advertisers: string[];
  metric: MediaMetric;
  onMetricChange: (metric: MediaMetric) => void;
  kind: DimensionKind;
  onKindChange: (kind: DimensionKind) => void;
  insights: OverIndexInsight[];
}

export const SpendByDimension = ({
  rows,
  advertisers,
  metric,
  onMetricChange,
  kind,
  onKindChange,
  insights,
}: SpendByDimensionProps) => {
  const { t } = useTranslation('analysis');
  const [index, setIndex] = useState(0);

  const columns = useMemo(() => displayDimensions(rows), [rows]);
  const barRows = useMemo(
    () => buildBarRows(rows, advertisers, columns, metric),
    [rows, advertisers, columns, metric],
  );

  const metricOptions: Array<{ value: MediaMetric; label: string }> = [
    { value: 'spend', label: t('analysisResult.mediaLandscape.totalSpend') },
    { value: 'impressions', label: t('analysisResult.mediaLandscape.impressions') },
    { value: 'cpm', label: t('analysisResult.mediaLandscape.avgCpm') },
  ];
  const kindOptions: Array<{ value: DimensionKind; label: string }> = [
    { value: 'channels', label: t('analysisResult.mediaLandscape.channels') },
    { value: 'publishers', label: t('analysisResult.mediaLandscape.publishers') },
  ];

  const showLegend = metric !== 'cpm' && columns.length > 0;
  const current = insights[carouselPosition(index, insights.length)];

  return (
    <div className="flex flex-col gap-4">
      <AnalysisSubHeader
        title={
          kind === 'publishers'
            ? t('analysisResult.mediaLandscape.spendByPublishers')
            : t('analysisResult.mediaLandscape.spendByChannels')
        }
        subtitle={
          kind === 'publishers'
            ? t('analysisResult.mediaLandscape.spendByPublishersSubtitle')
            : t('analysisResult.mediaLandscape.spendByChannelsSubtitle')
        }
        subtitleTone="meta"
      />
      <div className="flex flex-wrap items-center gap-2.5">
        <AnalysisSelect
          variant="filter"
          ariaLabel={t('analysisResult.mediaLandscape.metric')}
          value={metric}
          onValueChange={(next) => onMetricChange(next as MediaMetric)}
          options={metricOptions}
        />
        <AnalysisSelect
          variant="filter"
          ariaLabel={t('analysisResult.mediaLandscape.analyzeBy')}
          value={kind}
          onValueChange={(next) => onKindChange(next as DimensionKind)}
          options={kindOptions}
        />
      </div>
      <div className="flex flex-col items-stretch gap-5 rounded-xl border border-border-light bg-card p-4 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {showLegend && (
            <div data-testid="dimension-legend" className="flex flex-wrap gap-x-3.5 gap-y-1.5">
              {columns.map((dimension) => (
                <span
                  key={dimension}
                  className="flex items-center gap-1.5 text-xs text-foreground-alt"
                >
                  <span
                    className="size-2.5 rounded-[3px]"
                    style={{ backgroundColor: dimensionColor(dimension, columns) }}
                  />
                  {dimension}
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-col gap-3.5 pt-1">
            {barRows.map((bar) => (
              <div key={bar.advertiser} className="flex items-center gap-3">
                <span className="w-[118px] shrink-0 text-right text-sm text-foreground-alt">
                  {bar.advertiser}
                </span>
                <div className="flex flex-1 items-center gap-2.5">
                  <div
                    className="flex h-7 overflow-hidden rounded"
                    style={{ width: `${bar.trackPercent.toFixed(1)}%` }}
                  >
                    {bar.segments.length > 0 ? (
                      bar.segments.map((segment) => (
                        <span
                          key={segment.dimension}
                          title={`${segment.dimension} · ${formatSharePercent(segment.percent)}`}
                          style={{
                            width: `${segment.percent}%`,
                            backgroundColor: dimensionColor(segment.dimension, columns),
                          }}
                        />
                      ))
                    ) : (
                      <span className="w-full bg-primary" />
                    )}
                  </div>
                  <span className="whitespace-nowrap text-sm font-medium">
                    {formatMetricValue(bar.total, metric)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <InsightCarousel
          title={t('analysisResult.mediaLandscape.keyInsights')}
          count={insights.length}
          index={index}
          onIndexChange={setIndex}
        >
          {insights.length > 0 && (
            <>
              <span className="text-lg font-medium text-foreground">{current.advertiser}</span>
              <p className="flex-1 text-pretty text-sm leading-5 text-foreground-alt">
                {t('analysisResult.mediaLandscape.overIndexInsight', {
                  brand: current.advertiser,
                  dimension: current.dimension,
                  share: formatSharePercent(current.sharePercent),
                  marketShare: formatSharePercent(current.marketSharePercent),
                  multiple: current.multiple.toFixed(1),
                })}
              </p>
              <div className="flex gap-3 rounded-lg bg-card p-3">
                <div className="flex flex-1 flex-col gap-0.5">
                  <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                    {current.dimension}
                  </span>
                  <span className="text-lg font-medium">
                    {formatSharePercent(current.sharePercent)}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                    {t('analysisResult.mediaLandscape.marketAverage')}
                  </span>
                  <span className="text-lg font-medium">
                    {formatSharePercent(current.marketSharePercent)}
                  </span>
                </div>
              </div>
            </>
          )}
        </InsightCarousel>
      </div>
    </div>
  );
};
